// src/hooks/useChatMessages.ts
import { useState, useCallback, useMemo, useRef } from "react";
import { Message, ChatSummary } from "../types/chat";
import { formatDate } from "../utils/helpers";

export type MessagesMap = Record<string, Message[]>;

export const useChatMessages = (
  chat_id: number,
  externalId: string,
  messagesMap: MessagesMap,
  setMessagesMap: React.Dispatch<React.SetStateAction<MessagesMap>>,
  savedChats: ChatSummary[],
  setSavedChats: React.Dispatch<React.SetStateAction<ChatSummary[]>>,
  targetDbId: string,
  chatUniqueId: string
) => {
  const inputRef = useRef<string>('');
  const [renderedInput, setRenderedInput] = useState<string>('');
  const setInput = (val: string) => {
    inputRef.current = val;
    setRenderedInput(val);
  };

  const [isLoading, setLoading] = useState(false);

  const messages = useMemo(
    () => messagesMap[chatUniqueId] || [],
    [messagesMap, chatUniqueId]
  );

  const API = "https://zywajdscvz.a.pinggy.link";
  const LLM_API = " https://2d4cab6fac28.ngrok-free.app";

  const pushMessage = useCallback((msg: Message) => {
    setMessagesMap(prev => ({
      ...prev,
      [String(chatUniqueId)]: [...(prev[String(chatUniqueId)] || []), msg],
    }));
  }, [setMessagesMap, chatUniqueId]);

  const hasUserMessage = messages.some(m => m.type === "user");

  const sendMessage = useCallback(async () => {
    const text = inputRef.current.trim();
    if (!text || isLoading) return;
    setLoading(true);

    pushMessage({ id: crypto.randomUUID(), text, type: "user" });
    setInput("");


    try {
      await fetch(`${API}/storage/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chat_id, role: "user", content: text }),
      });

      const llmRes = await fetch(`${LLM_API}/chat/${targetDbId}/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        chat_id: chatUniqueId,
        message: text
      })
    });



      const llm = await llmRes.json();
      const rawPayload = llm.payload ?? {
        sql: llm.sql ?? null,
        result_preview: llm.result ?? null,
        explanation: llm.explanation ?? null,
      };
      const { sql, ...payload } = rawPayload;

      const hasExplanation = !!payload.explanation;
      const hasTable = Array.isArray(payload.result_preview) && payload.result_preview.length > 0;

      const botText = hasExplanation
  ? payload.explanation
  : hasTable
    ? "Ecco i risultati trovati:"
    : "Mi dispiace, non ho trovato alcuna informazione utile.";

// Prepariamo sempre un oggetto da salvare
const outputPayload = hasExplanation || hasTable
  ? payload
  : { message: botText };

pushMessage({
  id: crypto.randomUUID(),
  type: "bot",
  text: botText,
  timestamp: new Date().toISOString(),
  tableData: hasTable ? {
    headers: Object.keys(payload.result_preview[0]),
    rows: payload.result_preview,
  } : undefined,
  explanation: hasExplanation ? payload.explanation : undefined,
  isTableExpanded: false,
});

await fetch(`${API}/storage/messages`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    chat_id,
    role: "bot",
    content: botText,
    output: outputPayload,
  }),
});


      if (!hasUserMessage) {
        const res = await fetch(`${API}/api/generate-title`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text }),
        });
        const { title } = await res.json();
        const finalTitle = `${title} — ${targetDbId}`;
        const updatedChats = savedChats.map(c =>
          c.chatId === externalId ? { ...c, title: finalTitle || targetDbId } : c
        );
        setSavedChats(updatedChats);
        localStorage.setItem("savedChats", JSON.stringify(updatedChats));

        await fetch(`${API}/chats/${chat_id}/update-title`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title: finalTitle }),
        });
      }

    } catch (err) {
      console.error("sendMessage failed:", err);
      pushMessage({
        id: crypto.randomUUID(),
        text: `❌ ${(err as Error).message}`,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [renderedInput, isLoading, chatUniqueId, targetDbId, pushMessage, savedChats, hasUserMessage, setSavedChats]);

  return { input: renderedInput, setInput, messages, isLoading, sendMessage, pushMessage };
};
